import { Pane, Splitpanes } from 'splitpanes';
import AjaxUtils from '../../util/ajax_utils';
import { CodemirrorArea } from '../swagger/codemirror_area';
import { ApckDataMarkdown } from './markdown_apckdata';
import { ApckNarrativeAlerts } from './narrative_alerts';
import { ApckNarrativeMeta } from './narrative_meta';

const PLAYBACK_SPEED_OPTIONS = [
    { label: '0.5x', value: 0.5 },
    { label: '1.0x', value: 1 },
    { label: '1.5x', value: 1.5 },
    { label: '2.0x', value: 2 },

];

export function convertToRawHttpRequest(request) {
    const method = request.method;
    const url = new URL(request.target.url);
    const path = url.pathname + (url.search || '');
    const requestLine = `${method} ${path} HTTP/1.1`;

    const message = `${requestLine}\r\n${request.headers}\r\n\r\n${request.payload}`;
    return message.trim();
}

export function convertToRawHttpResponse(response) {
    const statusLine = `HTTP/1.1 ${response.status_code} ${response.status_text}`;
    const message = `${statusLine}\r\n${response.headers}\r\n\r\n${response.payload}`;
    return message.trim();
}

export function normaliseNarrativeFileType(value) {
    if (!value) return '';
    const fileType = value.toLowerCase();
    const typeMap = [
        { types: ['.json', 'json', 'application/json'], result: 'json' },
        { types: ['.png', 'png', 'image/png'], result: 'png' },
    ];

    const typeItem = typeMap.find((item) => item.types.includes(fileType));
    if (typeItem) return typeItem.result;

    return fileType;
}

export const DownloadImagesMixin = {
    methods: {
        async downloadImages(node) {
            if (Array.isArray(node)) {
                for (const item of node) {
                    await this.downloadImages(item);
                }
            } else if (typeof node === 'object' && node !== null) {
                if (
                    node.hasOwnProperty('object_path') &&
                    node.type === 'uploaded_evidence' &&
                    !node.download_url &&
                    !node._downloading &&
                    normaliseNarrativeFileType(node.file_type) === 'png'
                ) {
                    node._downloading = true;
                    const url = `${this.attachmentUrl}?attachment_id=${encodeURIComponent(node.object_path)}`;
                    try {
                        const data = await this.getImagePromise(url);
                        this.$set(node, 'download_url', data['download_url']);
                    } catch (err) {
                        node._downloading = false;
                    }
                }

                if (
                    node.hasOwnProperty('object_path') &&
                    node.type === 'uploaded_evidence' &&
                    !node.download_url &&
                    !node._downloading &&
                    node.serialised === 'ScreencastFrame'
                ) {
                    node._downloading = true;
                    const url = `${this.attachmentUrl}?attachment_id=${encodeURIComponent(node.object_path)}`;
                    try {
                        const data = await this.getImagePromise(url);
                        // download json
                        fetch(data['download_url'])
                            .then(response => response.json())
                            .then(attachmentData => {
                                if (attachmentData && attachmentData.data){
                                    this.$set(node, 'download_url', attachmentData.data);
                                    this.$set(node, 'meta_timestamp', attachmentData?.metadata?.timestamp);
                                }
                            });
                    } catch (err) {
                        node._downloading = false;
                    }
                }
                if (node.hasOwnProperty('content')) {
                    await this.downloadImages(node.content);
                }
            }
        },
        getImagePromise(url) {
            return new Promise((resolve, reject) => {
                AjaxUtils.ajax_get({
                    url,
                    success: resolve,
                    fail: reject,
                });
            });
        },
    },
};

export const DownloadMixin = {
    data() {
        return {
            content: {},
            loading: false,
        };
    },
    methods: {
        downloadContent() {
            const cmp = this;
            if (this.item.type === 'embedded_evidence') {
                this.content = this.item.content;
            } else {

                const url = `${this.attachmentUrl}?attachment_id=${encodeURIComponent(this.item.object_path)}`;
                this.loading = true;
                AjaxUtils.ajax_get({
                    url,
                    success: (json) => {
                        const downloadUrl = json['download_url'];
                        fetch(downloadUrl)
                            .then(response => response.json())
                            .then(attachmentData => {
                                cmp.content = attachmentData || {};
                                cmp.loading = false;
                            });
                    },
                });
            }
        },
    },
};

const VerbosityMixin = {
    methods: {
        getVerbosityColour(verbosity) {
            const map = {
                critical: 'red',
                error: 'red',
                warning: 'yellow',
                info: 'blue',
                debug: 'light-slate',
            };
            return map[verbosity?.toLowerCase()] || 'light-slate';
        },
    },
};

export const ApckNarrativeVerse = {
    mixins: [VerbosityMixin],
    props: {
        item: {
            type: Object,
        },
    },
    components: {
        ApckDataMarkdown,
        ApckNarrativeMeta,
    },
    template: `
        <ui-panel-base :color="getVerbosityColour(item.verbosity)" direction="column" gap="2">
            <apck-narrative-meta
                :timestamp="item.timestamp"
                :verbosity="item.verbosity"
                :file-type="item.file_type"
            />
            <apck-data-markdown
                v-if="item.markdown && item.markdown.elements"
                :elements="item.markdown.elements"
            />
        </ui-panel-base>
  `,
};

export const ModalMessagePairData = {
    mixins: [DownloadMixin],
    components: {
        'codemirror-area': CodemirrorArea,
        ApckNarrativeVerse,
        'splitpanes': Splitpanes,
        'pane': Pane,
    },
    props: {
        item: {
            type: Object,
        },
        attachmentUrl: {
            type: String,
        },
    },
    data() {
        return {
            content: {},
            loading: false,
        };
    },
    mounted() {
        this.downloadContent();
    },
    methods: {
        prettyJSON(obj) {
            try {
                return JSON.stringify(obj, null, 2);
            } catch (e) {
                return String(obj);
            }
        },
        formatRequest(content) {
            try {
                return convertToRawHttpRequest(content.request);
            } catch (e) {
                return this.prettyJSON(content.request);
            }
        },
        formatResponse(content) {
            try {
                return convertToRawHttpResponse(content.response);
            } catch (e) {
                return this.prettyJSON(content.response);
            }
        },
    },
    template: `
        <div class="new-ui">
        <ui-layout direction="column">
            <splitpanes class="default-theme">
                <pane>
                    <ui-heading>Request <span v-if="loading" class="fa fa-spinner fa-spin"></span></ui-heading>
                    <codemirror-area
                        :value="formatRequest(content)"
                        :read-only="true"
                        :line-wrapping="true"
                        bottom-height="450"
                    />
                </pane>
                <pane>
                    <ui-heading>Response <span v-if="loading" class="fa fa-spinner fa-spin"></span></ui-heading>
                    <codemirror-area
                        :value="formatResponse(content)"
                        :read-only="true"
                        :line-wrapping="true"
                        bottom-height="450"
                    />
                </pane>
            </splitpanes>
            <apck-narrative-verse
                :item="item"
            />
        </ui-layout>
        </div>
    `,
};

export const ModalMessageDefault = {
    mixins: [DownloadMixin],
    components: {
        'codemirror-area': CodemirrorArea,
        ApckNarrativeVerse
    },
    props: {
        item: {
            type: Object,
        },
        attachmentUrl: {
            type: String,
        },
    },
    data() {
        return {
            content: {},
        };
    },
    mounted() {
        this.downloadContent();
    },
    methods: {
        prettyJSON(obj) {
            try {
                return JSON.stringify(obj, null, 2);
            } catch (e) {
                return String(obj);
            }
        },
    },
    template: `
    <div class="new-ui">
        <ui-layout direction="column">
            <ui-heading>Data <span v-if="loading" class="fa fa-spinner fa-spin"></span></ui-heading>
            <codemirror-area
                :value="prettyJSON(content)"
                :read-only="true"
                :line-wrapping="true"
                bottom-height="450"
            />
            <apck-narrative-verse
                :item="item"
            />
        </ui-layout>
    </div>
    `,
};

export const ModalMessageSnapshot = {
    components: {
        ApckNarrativeVerse
    },
    props: {
        item: {
            type: Object,
        },
    },
    template: `
        <div class="new-ui">
            <ui-layout direction="column">
                <ui-image
                    :src="item.download_url"
                    placeholder="Loading..."
                    size="full-width"
                    aspect-ratio="landscape"
                    fit="contain"
                    :no-shadow="true"
                    :no-radius="true"
                />
                <apck-narrative-verse
                    :item="item"
                />
            </ui-layout>
        </div>

    `,
};

export const ApckNarrativeUploadedEvidence = {
    mixins: [VerbosityMixin],
    inject: {
        attachmentUrl: {},
        // Pass the event list to open the screencast from the log
        screencastEvents: { default: null },
    },
    components: {
        'apck-data-markdown': ApckDataMarkdown,
        'apck-narrative-meta': ApckNarrativeMeta,
        'modal-message-pair-data': ModalMessagePairData,
        'modal-message-default': ModalMessageDefault,
        'modal-message-snapshot': ModalMessageSnapshot,
    },
    props: {
        item: {
            type: Object,
        },
        // Pass the node to open the screencast with the narrative items list.
        parentNode: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            imageUrl: null,
        };
    },
    computed: {
        isImage() {
            const IMAGE_TYPES = ['png', 'image/png'];
            if (IMAGE_TYPES.includes(this.item.file_type)) return true;
            if (this.item.serialised && this.item.serialised === 'ScreencastFrame') return true;
            return false;
        },
    },
    mounted() {
        if (this.isImage) {
            this.loading = true;
        }
    },
    methods: {
        showModal() {
            // Open the screencast with events from the log or narrative items from the narrative output.
            if (this.item.serialised === 'ScreencastFrame' && (this.parentNode || this.screencastEvents)) {
                const bind = {
                    'attachment-url': this.attachmentUrl,
                    'start-id': this.item.object_path,
                };
                if (this.parentNode) {
                    bind.node = this.parentNode;
                }
                else bind.events = this.screencastEvents();

                this.$modal.open({
                    title: 'Screencast',
                    component: ModalScreencastGallery,
                    bind,
                    fullHeight: true,
                    size: 'lg',
                });
                return;
            }

            const fileType = normaliseNarrativeFileType(this.item.file_type);

            const fileTypeMap = {
                png: {
                    title: 'Snapshot',
                    component: ModalMessageSnapshot,
                    size: 'normal',
                },
                json: {
                    title: 'Message Data',
                    component: ModalMessageDefault,
                    size: 'wide',
                },
            };

            const serialisedMap = {
                MessagePairData: {
                    title: 'Message Data',
                    component: ModalMessagePairData,
                },
                ScreencastFrame: {
                    title: 'Snapshot',
                    component: ModalMessageSnapshot,
                    size: 'normal',
                }
                //serialised types
                // + MessagePairData
                // AuthLoginFlowDataSchema
                // SignedRequestAnalysisResultsData
                // SimpleTokenPossibleAuthToken
                // ResponseComparisonData
                // SimpleRequiredAuthTokens
                // SimpleTokenAnalysisResults
                // RuleSet
            };

            const config = fileTypeMap[fileType];

            if (config) {
                // If the file type is known, open the lightbox.
                // Otherwise, download the file.
                let serialisedConfig = {};

                if (fileType === 'json' && this.item.serialised) {
                    serialisedConfig = serialisedMap[this.item.serialised] || {};
                }

                const title = serialisedConfig.title || config.title;
                const component = serialisedConfig.component || config.component;
                const size = serialisedConfig.size || config.size;

                this.$modal.open({
                    title,
                    component,
                    bind: {
                        item: this.item,
                        attachmentUrl: this.attachmentUrl,
                    },
                    size: 'lg',
                });
            } else {
                // download file
                this.getLink(this.item);
            }
        },
        getLink(item) {
            const cmp = this;
            const url = `${this.attachmentUrl}?attachment_id=${encodeURIComponent(item.object_path)}`;
            AjaxUtils.ajax_get({
                url,
                success: (data) => {
                    // cmp.loading = false;
                    window.open(data['download_url'], '_blank');
                },
                fail: () => {
                    cmp.$flash({
                        type: 'error',
                        message: 'Attachment download failed.',
                    });
                },
            });
        },
    },
    template: `
        <ui-panel-base :color="getVerbosityColour(item.verbosity)" align="center" gap="2" :handler="showModal">
            <ui-layout direction="column" gap="2">
                <apck-narrative-meta
                    :timestamp="item.timestamp"
                    :verbosity="item.verbosity"
                    :file-type="item.file_type"
                />
                <apck-data-markdown
                    v-if="item.markdown && item.markdown.elements"
                    :elements="item.markdown.elements"
                />
            </ui-layout>
            <ui-layout v-if="isImage" justify="center" align="center">
                <ui-image
                    v-if="isImage"
                    :src="item.download_url"
                    placeholder="Loading..."
                    size="xs"
                />
            </ui-layout>


        </ui-panel-base>
  `,
};

export const ApckNarrativeNode = {
    name: 'apck-narrative-node',
    mixins: [VerbosityMixin],
    inject: {
        containerAsPanel: { default: false },
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        parentNode: {
            type: Object,
            default: null,
        },
        collapsible: {
            type: Boolean,
            default: true,
        },
        collapsed: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        'apck-data-markdown': ApckDataMarkdown,
        'apck-narrative-verse': ApckNarrativeVerse,
        'apck-narrative-uploaded-evidence': ApckNarrativeUploadedEvidence,
        'apck-narrative-meta': ApckNarrativeMeta,
    },
    data() {
        return {
            isCollapsed: this.collapsed,
        };
    },
    watch: {
        collapsed(val) { this.isCollapsed = val; },
    },
    computed: {
        isContainer() {
            return this.node.type === 'chapter' || this.node.type === 'scene';
        },
        containerTag() {
            if (this.containerAsPanel) return 'ui-panel-base';
            return 'ui-layout';
        },
        containerClass() {
            if (!this.containerAsPanel) return 'apck-narrative-container';
        },
    },
    methods: {
        toggle() {
            if (!this.collapsible) return;
            // When rendered as a panel, collapsing is controlled externally via the collapsed prop.
            if (this.containerAsPanel) return;
            this.isCollapsed = !this.isCollapsed;
        },
    },
    template: `
        <component v-if="isContainer" :is="containerTag" :color="getVerbosityColour(node.verbosity)" direction="column" gap="2" :class="containerClass">
            <ui-layout
                v-if="node.title"
                align="center"
                gap="2"
                :class="{ 'apck-narrative__item--collapsible': collapsible }"
                @click.native="toggle"
            >
                <ui-icon
                    v-if="collapsible"
                    :icon="isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"
                    type="secondary"
                />
                <ui-heading size="md">{{ node.title }}</ui-heading>
            </ui-layout>
            <apck-narrative-meta
                v-if="node.content.length===0"
                v-show="!isCollapsed"
                :timestamp="node.timestamp"
                :verbosity="node.verbosity"
            />
            <apck-data-markdown
                v-if="node.markdown && node.markdown.elements"
                v-show="!isCollapsed"
                :elements="node.markdown.elements"
            />
            <apck-narrative-node
                v-for="(child, index) in (node.content || [])"
                :key="index"
                v-show="!isCollapsed"
                :node="child"
                :parent-node="node"
            />
        </component>
        <apck-narrative-verse
        v-else-if="node.type === 'verse'"
        :item="node"
        />
        <apck-narrative-uploaded-evidence
        v-else-if="node.type === 'uploaded_evidence'"
        :item="node"
        :parent-node="parentNode"
        />
        <apck-narrative-uploaded-evidence
        v-else-if="node.type === 'embedded_evidence'"
        :item="node"
        :parent-node="parentNode"
        />
  `,
};

export const ModalScreencastGallery = {
    components: {
        'apck-narrative-node': ApckNarrativeNode,
        'apck-narrative-verse': ApckNarrativeVerse,
    },
    props: {
        events: {
            type: Array,
            default: () => [],
        },
        attachmentUrl: {
            type: String,
            default: '/vuln-control/attachment',
        },
        startId: {
            type: String,
            default: null,
        },
        node: {
            type: Object,
            default: null,
        },
    },
    provide() {
        return {
            attachmentUrl: this.attachmentUrl,
            containerAsPanel: true,
        };
    },
    data() {
        return {
            currentIndex: 0,
            playing: false,
            showNarrative: false,
            playbackSpeed: 1,
        };
    },
    created() {
        if (!this.startId) return;
        const startIndex = this.screencasts.findIndex(frame => frame.object_path === this.startId);
        if (startIndex >= 0) this.currentIndex = startIndex;
    },
    computed: {
        narrativeItems() {
            // Narrative items from the logs or narrative rendering.
            if (this.node) return this.node.content || [];
            return this.events
                .filter(item => item.type === 'narrative_item' && item.narrativeNode)
                .map(item => item.narrativeNode);
        },
        screencasts() {
            return this.narrativeItems.filter(n => n.serialised === 'ScreencastFrame');
        },
        currentFrame() {
            return this.screencasts[this.currentIndex] || null;
        },
        allNarrativeItems() {
            return this.narrativeItems.filter(n => n.serialised !== 'ScreencastFrame');
        },
        scrollTargetIndex() {
            if (!this.currentFrame || !this.allNarrativeItems.length) return -1;
            const ts = this.currentFrame.timestamp;
            let best = -1;
            for (let i = 0; i < this.allNarrativeItems.length; i++) {
                if (this.allNarrativeItems[i].timestamp <= ts) best = i;
                else break;
            }
            return best;
        },
        currentTitle() {
            const limit = this.scrollTargetIndex >= 0 ? this.scrollTargetIndex : -1;
            let chapter = null;
            let scene = null;
            for (let i = 0; i <= limit; i++) {
                const node = this.allNarrativeItems[i];
                if (node.type === 'chapter') {
                    chapter = node;
                    scene = null;
                } else if (node.type === 'scene') {
                    scene = node;
                }
            }
            const parts = [chapter?.title, scene?.title].filter(Boolean);
            if (!parts.length && this.node?.title) parts.push(this.node.title);
            return parts.join(' / ');
        },
        speedOptions() {
            return PLAYBACK_SPEED_OPTIONS;
        },
        verseClass() {
            return { 'apck-narrative-verse--fixed': !this.showNarrative };
        },
    },
    watch: {
        currentIndex() {
            this.$nextTick(() => this.scrollToTarget());
        },
    },
    beforeDestroy() {
        this.stopPlay();
    },
    methods: {
        scrollToTarget() {
            if (this.scrollTargetIndex < 0) return;
            const items = this.$refs.narrativeItems;
            if (!items || !items[this.scrollTargetIndex]) return;
            const el = items[this.scrollTargetIndex].$el || items[this.scrollTargetIndex];
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        prev() {
            this.stopPlay();
            if (this.currentIndex > 0) this.currentIndex--;
        },
        next() {
            this.stopPlay();
            if (this.currentIndex < this.screencasts.length - 1) this.currentIndex++;
        },
        togglePlay() {
            if (this.playing) {
                this.stopPlay();
            } else {
                this.startPlay();
            }
        },
        toggleNarrative() {
            this.showNarrative = !this.showNarrative;
        },
        openCurrentFrame() {
            if (!this.currentFrame) return;
            const url = this.currentFrame?.download_url;
            if (!url) return;
            if (!/^data:image\/.+;base64,/.test(url)) {
                window.open(url, '_blank');
                return;
            }
            const tab = window.open('', '_blank');
            if (!tab) return;
            const image = new Image();
            image.src = url;
            tab.document.body.append(image);
            tab.opener = null;
        },
        startPlay() {
            if (this.currentIndex >= this.screencasts.length - 1) this.currentIndex = 0;
            this.playing = true;
            this.scheduleNext();
        },
        stopPlay() {
            this.playing = false;
            clearTimeout(this._playTimer);
        },
        scheduleNext() {
            if (!this.playing) return;
            if (this.currentIndex >= this.screencasts.length - 1) {
                this.stopPlay();
                return;
            }
            const current = this.screencasts[this.currentIndex];
            const next = this.screencasts[this.currentIndex + 1];
            let delay = (next.meta_timestamp - current.meta_timestamp) * 1000 / this.playbackSpeed;
            this._playTimer = setTimeout(() => {
                this.currentIndex++;
                this.scheduleNext();
            }, delay);
        },
    },
    template: `
        <div class="new-ui h-full">
        <ui-layout direction="column" class="h-full">
            <ui-layout v-if="!screencasts.length" justify="center" align="center" class="grow">
                <span>No screenshots found.</span>
            </ui-layout>
            <ui-layout v-else direction="column" gap="2" class="grow" style="min-height:0">
                <ui-panel-base direction="row" align="center" justify="space-between" gap="2">
                    <span v-if="currentTitle" class="text--bold">{{ currentTitle }}</span>
                    <span v-else />
                    <i
                        :class="['fa-regular', showNarrative ? 'fa-arrow-right-to-line' : 'fa-arrow-left-to-line', 'icon', 'icon--md', 'icon--secondary']"
                        style="cursor:pointer"
                        :title="showNarrative ? 'Hide narrative panel' : 'Show narrative panel'"
                        @click="toggleNarrative"
                    />
                </ui-panel-base>
                <ui-layout align="stretch" class="grow" style="min-height:0">
                    <ui-layout direction="column" gap="2" class="grow" style="min-height:0">
                        <ui-image
                            v-if="currentFrame"
                            :src="currentFrame.download_url"
                            placeholder="Loading..."
                            size="full-width"
                            aspect-ratio="landscape"
                            fit="contain"
                            :no-shadow="true"
                            :no-radius="true"
                        />
                        <ui-panel-base
                            v-if="currentFrame"
                            direction="row"
                            align="center"
                            justify="space-between"
                            gap="2"
                        >
                            <ui-layout align="center" gap="2">
                                <ui-button
                                    label="Previous"
                                    hideLabel
                                    iconLeft="fa-chevron-left"
                                    type="outline"
                                    size="sm"
                                    :disabled="currentIndex === 0"
                                    @click="prev"
                                />
                                <ui-button
                                    :label="playing ? 'Pause' : 'Play'"
                                    hideLabel
                                    :iconLeft="playing ? 'fa-pause' : 'fa-play'"
                                    type="outline"
                                    size="sm"
                                    @click="togglePlay"
                                />
                                <span class="text--secondary">{{ currentIndex + 1 }} / {{ screencasts.length }}</span>
                                <ui-button
                                    label="Next"
                                    hideLabel
                                    iconLeft="fa-chevron-right"
                                    type="outline"
                                    size="sm"
                                    :disabled="currentIndex === screencasts.length - 1"
                                    @click="next"
                                />
                            </ui-layout>
                            <ui-layout align="center" gap="2">
                                <ui-dropdown-select
                                    :modelValue="playbackSpeed"
                                    @update:modelValue="playbackSpeed = $event"
                                    :options="speedOptions"
                                    icon-left="fa-gauge"
                                />
                                <ui-button
                                    label="Open image"
                                    hideLabel
                                    iconLeft="fa-external-link"
                                    type="outline"
                                    size="sm"
                                    @click="openCurrentFrame"
                                />
                            </ui-layout>
                        </ui-panel-base>
                        <div v-if="currentFrame" :class="verseClass">
                            <apck-narrative-verse
                                :item="currentFrame"
                            />
                        </div>
                    </ui-layout>
                    <ui-layout v-if="showNarrative" direction="column" gap="2" class="grow" style="overflow-y:auto">
                        <apck-narrative-node
                            v-for="(node, i) in allNarrativeItems"
                            :key="i"
                            ref="narrativeItems"
                            :node="node"
                            :collapsible="false"
                        />
                        <span v-if="!allNarrativeItems.length">No narrative items.</span>
                    </ui-layout>
                </ui-layout>
            </ui-layout>
        </ui-layout>
        </div>
    `,
};

export const ApckNarrative = {
    mixins: [DownloadImagesMixin],
    components: { ApckNarrativeNode, ApckDataMarkdown, ApckNarrativeAlerts },
    props: {
        narrative: {
            type: Object,
            default: {},
        },
        scanSettingsUrl: {
            type: String,
            default: null,
        },
        goScriptUrl: {
            type: String,
            default: null,
        },
        attachmentUrl: {
            type: String,
            default: '/vuln-control/attachment',
        },
        containerAsPanel: {
            // Render the container as a panel
            type: Boolean,
            default: false,
        }
    },
    provide() {
        return {
            attachmentUrl: this.attachmentUrl,
            containerAsPanel: this.containerAsPanel,
        };
    },
    data() {
        return {
            verbosityLevel: 'DEBUG',
            VERBOSITY_LEVELS: {
                CRITICAL: 50,
                ERROR: 40,
                WARNING: 30,
                INFO: 20,
                DEBUG: 10,
            },
        };
    },
    watch: {
        narrative: {
            handler(newVal) {
                this.downloadImages(newVal);
            },
            immediate: true
        }
    },
    methods: {
        scrollToError() {
            this.$nextTick(() => {
                const els = this.$el.querySelectorAll('.badge--red');
                if (!els.length) return;
                els[els.length - 1].scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },
        openImageModal(node) {
            this.$modal.open({
                title: 'Snapshot',
                component: ModalMessageSnapshot,
                bind: { item: node },
                size: 'lg',
            });
        },
    },
    computed: {
        levelNames() {
            return Object.keys(this.VERBOSITY_LEVELS);
        },
        // filteredContent() {
        //     // This filter is currently not in use
        //     // TODO: go through the nodes separately
        //     // and mark parent nodes that should be visible when a higher verbosity level is inside.
        //     // <apck-select v-model="verbosityLevel" :options="levelNames" label="Verbosity Level:" />
        //     const currentLevel = this.VERBOSITY_LEVELS[this.verbosityLevel] || 0;
        //     const filterByLevel = (items) => {
        //         return items
        //             .filter((item) => {
        //                 const level = this.VERBOSITY_LEVELS[item.verbosity] || 0;
        //                 if (item.type === 'chapter' || item.type === 'scene') return true;
        //                 return level >= currentLevel;
        //             })
        //             .map((item) => {
        //                 if (Array.isArray(item.content)) {
        //                     return { ...item, content: filterByLevel(item.content) };
        //                 }
        //                 return item;
        //             });
        //     };
        //     return filterByLevel(this.narrative.content || []);
        // },
    },
    template: `
        <div class="new-ui">
        <ui-layout direction="column" gap="2" class="apck-narrative-new">
            <apck-narrative-alerts
                :narrative="narrative"
                :scan-settings-url="scanSettingsUrl"
                :go-script-url="goScriptUrl"
                @details="scrollToError"
                @view-image="openImageModal"
            />
            <ui-heading v-if="narrative.title" size="xl">{{ narrative.title }}</ui-heading>
            <apck-data-markdown
                v-if="narrative.markdown && narrative.markdown.elements"
                :elements="narrative.markdown.elements"
            />
            <ui-layout v-if="narrative.content" direction="column" gap="2">
                <apck-narrative-node
                    v-for="(item, index) in narrative.content"
                    :key="index"
                    :node="item"
                />
            </ui-layout>
            <p v-else class="text--secondary">
                No messages found for <strong>{{ verbosityLevel }}</strong> verbosity. Please select a different level.
            </p>
        </ui-layout>
        </div>
  `,
};
